import { Close, Search } from "@mui/icons-material";
import { useEffect, useMemo, useState } from "react";
import { CircularProgress, IconButton } from "@mui/material";

import Box from "../Box";
import Modal from "../Modal";
import Avatar from "../Avatar";
import CheckBox from "../CheckBox";
import FormField from "../FormField";
import Typography from "../Typography";
import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";
import Resources from "../../constants/resources";
import CustomPagination from "../CustomPagination";
import UserService, { IRequestGetPagedUser } from "../../services/identity/user.service";

import { Status } from "../../constants/enums";
import { Constants } from "../../constants/constants";
import { useCommonComponentContext } from "../../providers/CommonComponentProvider";



export interface IRecordUserProps {
    id: string;
    email?: string;
    userCode?: string;
    fullName?: string;
    avatarId?: string;
    avatarUrl?: string;
    phoneNumber?: string;
}

const ModalSearchUser = ({
    open,
    userIds,
    isSingle,
    titleModal,
    userIdsDisable,
    organizationId,
    onClose, onAction,
}: {
    open: boolean;
    isSingle?: boolean;
    titleModal?: string;

    userIds: string[];
    organizationId?: string;
    userIdsDisable?: string[];

    onClose: () => void;
    onAction: (data: IRecordUserProps[]) => void;
}) => {

    const Strings = useStrings();

    const {
        clientId,
        onError,
    } = useCommonComponentContext();

    const [loading, setLoading] = useState<boolean>(false);

    const [dataRows, setDataRows] = useState<IRecordUserProps[]>([]);
    const [dataSelect, setDataSelect] = useState<IRecordUserProps[]>([]);

    const [requestGetPaged, setRequestGetPaged] = useState<{
        searchText?: string,
        pageNumber?: number,
        totalCount?: number,
    }>({
        pageNumber: 1,
        totalCount: 0,
    });

    useEffect(() => {
        if (open) {
            getPaged(requestGetPaged);
        } else {
            setDataRows([]);
            setRequestGetPaged({
                pageNumber: 1,
                totalCount: 0,
            });
        }
    }, [open, userIds]);

    const textButtonAction = useMemo(() => {
        if (isSingle) {
            return Strings.Common.SELECT;
        } else {
            return `${Strings.Common.SELECT} ${(dataSelect.length > 0) ? dataSelect.length : ""}`;
        }
    }, [Strings, isSingle, dataSelect.length]);

    const getPaged = async (newReq?: any) => {
        try {
            setLoading(true);

            const pageSize = Constants.ROW_PER_PAGE;
            const pageNumber = Helpers.getPageNumber(newReq?.pageNumber || 1, pageSize, newReq?.totalCount || 0) || 1;

            const newQuery: IRequestGetPagedUser = {
                pageSize: pageSize,
                pageNumber: pageNumber,

                listStatus: [Status.Active],
                searchText: newReq?.searchText || undefined,

                clientId: clientId,
                organizationId: organizationId || 0,
            };

            const resultGetpaged = await UserService.getPaged(newQuery);

            const newRow: IRecordUserProps[] = [];
            const newDataItem: IRecordUserProps[] = [];

            [...resultGetpaged?.items || []].forEach(item => {
                let newValueItem: IRecordUserProps;

                if (Helpers.isNullOrEmpty(item.organizationUserProfile)
                    || Object.keys(item.organizationUserProfile).length === 0) {
                    newValueItem = {
                        id: item.id,
                        email: item.email,
                        // userCode: item.userCode,
                        avatarId: item.avatarId,
                        avatarUrl: item.avatarUrl,
                        phoneNumber: item.phoneNumber,
                        fullName: item.fullName || item.email,
                    };
                } else {
                    let fullName = item.fullName || item.userName;

                    if (!Helpers.isNullOrEmpty(item.organizationUserProfile?.firstName)
                        || !Helpers.isNullOrEmpty(item.organizationUserProfile?.lastName)) {
                        fullName = `${item.organizationUserProfile?.lastName || ""} ${item.organizationUserProfile?.firstName || ""}`.trim();
                    }

                    newValueItem = {
                        avatarId: item.avatarId,
                        avatarUrl: item.avatarUrl,
                        id: item.organizationUserProfile?.userId,
                        email: item.organizationUserProfile?.email,
                        userCode: item.organizationUserProfile?.userCode,
                        phoneNumber: item.organizationUserProfile?.phoneNumber,
                        fullName: fullName,
                    };
                }

                if ([...userIds || []].includes(item.id)) {
                    newDataItem.push(newValueItem);
                };

                newRow.push(newValueItem);
            });

            setDataRows(newRow);
            setDataSelect(newDataItem);

            setRequestGetPaged({
                searchText: newQuery?.searchText,
                totalCount: resultGetpaged?.totalCount,
                pageNumber: resultGetpaged?.currentPage,
            });

        } catch (error) {
            const err = Helpers.renderExceptionError(error);
            onError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectedById = (item: any) => {
        let newData = [...dataSelect || []];

        const indexTemp = dataSelect?.findIndex(el => el.id === item.id);

        if (isSingle) {
            if (indexTemp === -1) {
                newData = [item];
            } else {
                newData = [];
            }
        } else {

            if (indexTemp === -1) {
                newData.push(item);
            } else {
                newData.splice(indexTemp, 1);
            }
        }
        setDataSelect(newData);
    }

    return (
        <>
            <Modal
                fullWidth
                maxWidth="md"
                visible={open}
                hasActionButton
                buttonAction={textButtonAction}
                title={titleModal || Strings.USER.ADD_NEW}

                onClose={onClose}
                onAction={() => { onAction(dataSelect); }}
            >
                <>
                    <Box>
                        {/* input search text by getpage */}
                        <FormField
                            maxLength={255}
                            variant="outlined"
                            placeholder={Strings.Common.SEARCH}
                            defaultValue={requestGetPaged?.searchText || ""}
                            onBlur={(value: any) => {
                                getPaged({ ...requestGetPaged, searchText: value });
                            }}
                            onKeyPress={(e: any) => {
                                if (e.key === "Enter") {
                                    getPaged({ ...requestGetPaged, searchText: e?.target?.value });
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <IconButton
                                        color="info"
                                        onClick={() => {
                                            getPaged({ ...requestGetPaged });
                                        }}
                                    >
                                        <Search />
                                    </IconButton>
                                ),
                                endAdornment: requestGetPaged?.searchText && (
                                    <IconButton
                                        color="secondary"
                                        onClick={() => {
                                            getPaged({ searchText: "", pageNumber: 1 });
                                        }}
                                    >
                                        <Close />
                                    </IconButton>
                                ),
                            }}
                        />
                    </Box>
                    {/* list record */}
                    <Box sx={{
                        marginTop: 2,
                        height: "48vh",
                        overflow: "auto",
                    }}>
                        {loading
                            ? (<LoadingInModal height="48vh" />)
                            : ((dataRows.length === 0)
                                ? (
                                    <Box sx={{ textAlign: "center" }}>
                                        <Typography variant="button" color="secondary">
                                            {Strings.format(Strings.USER.NO_DATA_USER)}
                                        </Typography>
                                    </Box>
                                )
                                : (
                                    <Box>
                                        <Box>
                                            {
                                                dataRows.map((item) => (
                                                    <Box
                                                        key={item.id}
                                                        onClick={() => {
                                                            ![...userIdsDisable || []].includes(item.id) &&
                                                                handleSelectedById(item);
                                                        }}
                                                        sx={(theme) => {
                                                            const { functions: { rgba }, palette: { info } } = theme;
                                                            const isDisable = [...userIdsDisable || []].includes(item.id);
                                                            const isChoosed = dataSelect?.findIndex(el => el.id === item.id) !== -1;
                                                            return {
                                                                padding: "8px",
                                                                display: "flex",
                                                                flexWrap: "wrap",
                                                                minHeight: "80px",
                                                                borderRadius: "8px",
                                                                marginBottom: "8px",
                                                                alignItems: "center",
                                                                border: `1px ${isDisable ? "#c3c3c3" : (isChoosed ? info.main : "#c3c3c3")} solid`,
                                                                backgroundColor: isDisable ? "#ededed" : (isChoosed ? rgba(info.main, 0.1) : "transparent"),
                                                                ":hover": !isDisable && {
                                                                    cursor: "pointer",
                                                                    border: `1px ${info.main} solid`,
                                                                    backgroundColor: rgba(info.main, 0.1),
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        <CheckBox
                                                            sx={{ width: "50px" }}
                                                            disabled={[...userIdsDisable || []].includes(item.id)}
                                                            checked={(dataSelect?.findIndex(el => el.id === item.id) !== -1)}
                                                        />
                                                        <Box sx={{
                                                            display: "flex",
                                                            flexWrap: "wrap",
                                                            alignItems: "center",
                                                            width: { xs: "calc(100% - 50px)", sm: "auto" },
                                                        }}>
                                                            <Avatar
                                                                src={item.avatarUrl}
                                                                text={item?.fullName}
                                                                sx={{
                                                                    marginLeft: "16px",
                                                                    marginRight: "16px",
                                                                    border: "1px #dddddd solid",
                                                                    display: { xs: "none", sm: "flex" },
                                                                }}
                                                            />

                                                            <Box display="inline-grid">
                                                                <Typography variant="button" fontWeight="bold" sx={{
                                                                    overflow: "hidden",
                                                                    whiteSpace: "nowrap",
                                                                    textOverflow: "ellipsis",
                                                                }}>
                                                                    {
                                                                        Helpers.isNullOrEmpty(item?.userCode)
                                                                            ? `${item?.fullName || "No Name"}`
                                                                            : `${item?.userCode} - ${item?.fullName || item?.email || "No Name"}`
                                                                    }
                                                                </Typography>
                                                                {
                                                                    !Helpers.isNullOrEmpty(item?.phoneNumber) &&
                                                                    <Typography variant="button" color="secondary" sx={{
                                                                        overflow: "hidden",
                                                                        whiteSpace: "nowrap",
                                                                        textOverflow: "ellipsis",
                                                                    }}>
                                                                        {"sđt"}:&nbsp;{item?.phoneNumber}
                                                                    </Typography>
                                                                }
                                                                {
                                                                    !Helpers.isNullOrEmpty(item?.email) &&
                                                                    <Typography variant="button" color="secondary" sx={{
                                                                        overflow: "hidden",
                                                                        whiteSpace: "nowrap",
                                                                        textOverflow: "ellipsis",
                                                                    }}>
                                                                        {"email"}:&nbsp;{item?.email}
                                                                    </Typography>
                                                                }
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                ))
                                            }
                                        </Box>
                                        <Box mt={2}>
                                            <CustomPagination
                                                rowsPerPageOptions={[20]}
                                                pageSize={Constants.ROW_PER_PAGE}
                                                page={requestGetPaged?.pageNumber}
                                                total={requestGetPaged?.totalCount}
                                                onChangePage={(value) => {
                                                    getPaged({ ...requestGetPaged, pageNumber: value });
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                )
                            )
                        }
                    </Box>
                </>
            </Modal>
        </>
    );
};

export default ModalSearchUser;

export const LoadingInModal = ({ height }: { height?: string }) => {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: height ? height : "auto",
            }}
        >
            <CircularProgress color="info" />
        </Box>
    );
};